import {
  RECEIPT_CHANNELS,
  RECEIPT_CHANNEL_LABELS,
  RECEIPT_STATUSES,
  RECEIPT_STATUS_LABELS,
  type ReceiptChannel,
  type ReceiptListQuery,
  type ReceiptStatus,
} from '@chukua/common';
import { DatePicker, Select } from 'antd';
import dayjs from 'dayjs';
import type { ListQueryControls } from '../../../../../shared/hooks/use-list-query';
import { useAllCompanyAccountOptions } from '../../../../company-accounts/public/use-all-company-account-options';

const DATE_FORMAT = 'YYYY-MM-DD';

const CHANNEL_OPTIONS = RECEIPT_CHANNELS.map((channel) => ({ value: channel, label: RECEIPT_CHANNEL_LABELS[channel] }));

const STATUS_OPTIONS = RECEIPT_STATUSES.map((status) => ({ value: status, label: RECEIPT_STATUS_LABELS[status] }));

/** Channel, status, account and the date range narrow the receipts on the server; each lives in the URL. */
export function ReceiptFilters({ list }: { list: ListQueryControls<ReceiptListQuery> }) {
  const { query } = list;
  const accounts = useAllCompanyAccountOptions();
  const range: [dayjs.Dayjs | null, dayjs.Dayjs | null] = [
    query.receivedFrom ? dayjs(query.receivedFrom) : null,
    query.receivedTo ? dayjs(query.receivedTo) : null,
  ];

  return (
    <div className="flex flex-wrap items-end gap-3">
      <Select<ReceiptChannel[]>
        mode="multiple"
        className="w-full sm:w-56"
        aria-label="Channel"
        placeholder="Channel"
        value={query.channel ?? []}
        options={CHANNEL_OPTIONS}
        onChange={(channel) => list.setQuery({ channel })}
      />
      <Select<ReceiptStatus[]>
        mode="multiple"
        className="w-full sm:w-56"
        aria-label="Status"
        placeholder="Status"
        value={query.status ?? []}
        options={STATUS_OPTIONS}
        onChange={(status) => list.setQuery({ status })}
      />
      <Select<string[]>
        mode="multiple"
        className="w-full sm:w-64"
        aria-label="Account"
        placeholder="Account"
        value={query.companyAccountId ?? []}
        options={accounts.map((account) => ({ value: account.id, label: `${account.name} · ${account.detail}${account.isRetired ? ' (retired)' : ''}` }))}
        onChange={(companyAccountId) => list.setQuery({ companyAccountId })}
      />
      <DatePicker.RangePicker
        className="w-full sm:w-72"
        aria-label="Received between"
        placeholder={['Received from', 'Received to']}
        value={range}
        disabledDate={(day) => day.isAfter(dayjs(), 'day')}
        onChange={(days) => list.setQuery({ receivedFrom: days?.[0]?.format(DATE_FORMAT), receivedTo: days?.[1]?.format(DATE_FORMAT) })}
      />
    </div>
  );
}
